import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import moment from 'moment';
import '../styles/EventPopup.css';
import ReactDOM from 'react-dom';

const EventPopup = React.memo(({ 
    isOpen, 
    onClose, 
    onSave, 
    onDelete,
    employee,
    startTime,
    endTime,
    date,
    existingEvent,
    prefilledParticipants = [],
    employees = [] // Accept employees as prop instead of fetching
}) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [location, setLocation] = useState('');
    const [isAllDay, setIsAllDay] = useState(false);
    const [isConferencing, setIsConferencing] = useState(false);
    const [isRepeat, setIsRepeat] = useState(false);
    const [selectedColor, setSelectedColor] = useState('#4263eb');
    const [participantSearch, setParticipantSearch] = useState('');
    const [error, setError] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isRightColumn, setIsRightColumn] = useState(false); // New state for layout mode
    const fileInputRef = useRef(null);
    
    // Internal time state for managing time inputs
    const [internalStartTime, setInternalStartTime] = useState('');
    const [internalEndTime, setInternalEndTime] = useState('');
    
    // Internal date state for managing date selection
    const [internalDate, setInternalDate] = useState(null);
    
    // Zoom conferencing state
    const [zoomInfo, setZoomInfo] = useState(null);
    const [isCreatingZoom, setIsCreatingZoom] = useState(false);

    // Participants state
    const [selectedParticipants, setSelectedParticipants] = useState([]);
    const [showParticipantsDropdown, setShowParticipantsDropdown] = useState(false);
    const participantInputRef = useRef(null);

    // Repeat dropdown state
    const [repeatFrequency, setRepeatFrequency] = useState('daily');
    const [showRepeatDropdown, setShowRepeatDropdown] = useState(false);
    const repeatDropdownRef = useRef(null);

    // Repeat options
    const repeatOptions = [
        { value: 'daily', label: 'Every day' },
        { value: 'every-other-day', label: 'Every other day' },
        { value: 'weekly', label: 'Every week' },
        { value: 'monthly', label: 'Every month' }
    ];

    // Memoize all callback functions at the top
    const formatFileSize = useCallback((bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }, []);

    const handleFileUpload = useCallback((files) => {
        const newFiles = Array.from(files).map(file => ({
            file,
            name: file.name,
            size: file.size,
            type: file.type
        }));
        setUploadedFiles(prev => [...prev, ...newFiles]);
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFileUpload(files);
        }
    }, [handleFileUpload]);

    const handleFileInputChange = useCallback((e) => {
        const files = e.target.files;
        if (files.length > 0) {
            handleFileUpload(files);
        }
    }, [handleFileUpload]);

    const removeFile = useCallback((index) => {
        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    }, []);

    // Memoize filtered employees to prevent unnecessary recalculations
    const filteredEmployees = useMemo(() => {
        if (participantSearch.trim() === '') {
            return [];
        }

        return employees.filter(emp => {
            const searchTerm = participantSearch.toLowerCase();
            const fullName = `${emp.first_name || ''} ${emp.last_name || ''}`.toLowerCase().trim();
            const email = emp.email.toLowerCase();
            
            // Don't show already selected participants
            const isAlreadySelected = selectedParticipants.some(p => p.email === emp.email);
            
            return !isAlreadySelected && (
                email.includes(searchTerm) ||
                fullName.includes(searchTerm) ||
                (emp.first_name && emp.first_name.toLowerCase().includes(searchTerm)) ||
                (emp.last_name && emp.last_name.toLowerCase().includes(searchTerm))
            );
        });
    }, [participantSearch, employees, selectedParticipants]);

    // Memoize callback functions to prevent unnecessary re-renders
    const handleParticipantSelect = useCallback((employee) => {
        console.log('Selecting participant:', employee);
        setSelectedParticipants(prev => [...prev, employee]);
        setParticipantSearch('');
        setShowParticipantsDropdown(false);
        participantInputRef.current?.focus();
    }, []);

    const handleParticipantRemove = useCallback((emailToRemove) => {
        setSelectedParticipants(prev => prev.filter(p => p.email !== emailToRemove));
    }, []);

    const handleParticipantInputFocus = useCallback(() => {
        if (participantSearch.trim() !== '' && filteredEmployees.length > 0) {
            setShowParticipantsDropdown(true);
        }
    }, [participantSearch, filteredEmployees]);

    const handleParticipantInputBlur = useCallback(() => {
        // Delay hiding dropdown to allow for clicks
        setTimeout(() => {
            setShowParticipantsDropdown(false);
        }, 200);
    }, []);

    // Handle repeat button click
    const handleRepeatClick = useCallback(() => {
        console.log('🔄 handleRepeatClick called, current isRepeat:', isRepeat);
        if (!isRepeat) {
            console.log('🔄 Setting isRepeat to true and showing dropdown');
            setIsRepeat(true);
            setShowRepeatDropdown(true);
        } else {
            console.log('🔄 Setting isRepeat to false and hiding dropdown');
            setIsRepeat(false);
            setShowRepeatDropdown(false);
        }
    }, [isRepeat]);

    // Handle repeat frequency selection
    const handleRepeatFrequencySelect = useCallback((frequency) => {
        setRepeatFrequency(frequency);
        setShowRepeatDropdown(false);
    }, []);

    // Generate recurrence rule based on repeat frequency
    const generateRecurrenceRule = useCallback((frequency, startDate) => {
        console.log('🔄 generateRecurrenceRule called with:', { frequency, startDate, isRepeat });
        
        if (!frequency || !isRepeat) {
            console.log('🔄 generateRecurrenceRule returning null because:', { frequency: !!frequency, isRepeat });
            return null;
        }

        const baseRule = 'RRULE:';
        let rule;
        switch (frequency) {
            case 'daily':
                rule = `${baseRule}FREQ=DAILY`;
                break;
            case 'every-other-day':
                rule = `${baseRule}FREQ=DAILY;INTERVAL=2`;
                break;
            case 'weekly':
                rule = `${baseRule}FREQ=WEEKLY`;
                break;
            case 'monthly':
                rule = `${baseRule}FREQ=MONTHLY`;
                break;
            default:
                rule = null;
        }
        
        console.log('🔄 generateRecurrenceRule returning:', rule);
        return rule;
    }, [isRepeat]);

    // Parse existing recurrence rule to determine frequency
    const parseRecurrenceRule = useCallback((recurrenceRule) => {
        if (!recurrenceRule) return { isRepeat: false, frequency: 'daily' };

        const rule = recurrenceRule.toLowerCase();
        
        if (rule.includes('freq=daily') && rule.includes('interval=2')) {
            return { isRepeat: true, frequency: 'every-other-day' };
        } else if (rule.includes('freq=daily')) {
            return { isRepeat: true, frequency: 'daily' };
        } else if (rule.includes('freq=weekly')) {
            return { isRepeat: true, frequency: 'weekly' };
        } else if (rule.includes('freq=monthly')) {
            return { isRepeat: true, frequency: 'monthly' };
        }
        
        return { isRepeat: false, frequency: 'daily' };
    }, []);

    // Handle clicks outside the dropdown to close it
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (repeatDropdownRef.current && !repeatDropdownRef.current.contains(event.target)) {
                setShowRepeatDropdown(false);
            }
        };

        if (showRepeatDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showRepeatDropdown]);

    // Update dropdown visibility based on filtered results
    useEffect(() => {
        setShowParticipantsDropdown(filteredEmployees.length > 0);
    }, [filteredEmployees]);

    // Add debugging when props change - optimized to only log when actually needed
    useEffect(() => {
        console.log('🎭 EventPopup useEffect triggered with props:', {
            isOpen,
            existingEvent: !!existingEvent,
            startTime,
            endTime,
            date,
            employees: employees.length
        });

        if (!isOpen) {
            console.log('🎭 EventPopup not open, skipping effect');
            return;
        }

        if (isOpen && (startTime || endTime)) {
            console.log('🎭 EventPopup received props:', {
                startTime,
                endTime,
                date: date ? date.toString() : 'null',
                existingEvent: existingEvent ? 'present' : 'null'
            });
        }
    }, [isOpen, startTime, endTime, date, existingEvent, employees.length]);

    // Reset form when popup opens/closes - optimized dependencies
    useEffect(() => {
        if (!isOpen) return;

        if (existingEvent && !existingEvent.isNew) {
            console.log('📋 Loading existing event data:', existingEvent);
            
            // Populate form with existing event data
            setTitle(existingEvent.title || '');
            setDescription(existingEvent.description || '');
            setLocation(existingEvent.location || '');
            setSelectedColor(existingEvent.color || '#4263eb');
            
            // Set the all-day flag from existing event data
            setIsAllDay(existingEvent.isallday || existingEvent.is_all_day || false);
            
            // Parse recurrence rule if it exists
            if (existingEvent.recurrence_rule) {
                console.log('🔄 Parsing recurrence rule:', existingEvent.recurrence_rule);
                const { isRepeat, frequency } = parseRecurrenceRule(existingEvent.recurrence_rule);
                setIsRepeat(isRepeat);
                setRepeatFrequency(frequency);
                console.log('🔄 Set repeat state:', { isRepeat, frequency });
            } else {
                setIsRepeat(false);
                setRepeatFrequency('daily');
            }
            
            // Load existing participants if any - check both locations for participant data
            let participantEmails = [];
            if (existingEvent.raw && existingEvent.raw.participants && Array.isArray(existingEvent.raw.participants)) {
                participantEmails = existingEvent.raw.participants;
                console.log('🎯 Loading participants from raw field:', participantEmails);
            } else if (existingEvent.participants && Array.isArray(existingEvent.participants)) {
                participantEmails = existingEvent.participants;
                console.log('🎯 Loading participants from direct field:', participantEmails);
            }
            
            if (participantEmails.length > 0 && employees.length > 0) {
                console.log('🎯 Processing participant emails:', participantEmails);
                // Convert email strings to participant objects
                const participantObjects = participantEmails.map(email => {
                    // Try to find the employee object for this email
                    const employee = employees.find(emp => emp.email === email);
                    if (employee) {
                        console.log('🎯 Found employee for email:', email, employee);
                        return employee;
                    } else {
                        console.log('🎯 Creating placeholder for email:', email);
                        return { email, first_name: '', last_name: '' };
                    }
                });
                console.log('🎯 Setting participant objects:', participantObjects);
                setSelectedParticipants(participantObjects);
            } else {
                console.log('🎯 No participants found or employees not loaded');
                setSelectedParticipants([]);
            }
            
            // Load existing files if any
            if (existingEvent.files && Array.isArray(existingEvent.files)) {
                setUploadedFiles(existingEvent.files);
            }
            // Load existing Zoom info if any
            if (existingEvent.zoom_info) {
                console.log('🎯 Loading existing Zoom info:', existingEvent.zoom_info);
                setZoomInfo(existingEvent.zoom_info);
                setIsConferencing(true);
            } else {
                console.log('🎯 No Zoom info found in existing event');
                setZoomInfo(null);
                setIsConferencing(false);
            }
        } else if (existingEvent && existingEvent.isNew) {
            console.log('📋 Loading new event data from EventPreview expansion:', existingEvent);
            
            // This is a new event coming from EventPreview expansion
            setTitle(existingEvent.title || '');
            setDescription(existingEvent.description || '');
            setLocation(existingEvent.location || '');
            setSelectedColor(existingEvent.color || '#4263eb');
            
            // Set times from existingEvent
            if (existingEvent.startTime && existingEvent.endTime) {
                setInternalStartTime(existingEvent.startTime);
                setInternalEndTime(existingEvent.endTime);
            } else {
                setInternalStartTime(startTime || '');
                setInternalEndTime(endTime || '');
            }
            
            // Set date from props
            setInternalDate(date || null);
            
            // Handle all-day detection
            const shouldBeAllDay = 
                (existingEvent.startTime === '00:00' && existingEvent.endTime === '23:59') ||
                (startTime === '00:00' && endTime === '23:59');
            setIsAllDay(shouldBeAllDay);
            
            // Handle participants from EventPreview
            if (existingEvent.participants && Array.isArray(existingEvent.participants) && employees.length > 0) {
                console.log('🎯 Processing participants from EventPreview expansion:', existingEvent.participants);
                
                // Convert email strings to participant objects
                const participantObjects = existingEvent.participants.map(email => {
                    const employee = employees.find(emp => emp.email === email);
                    if (employee) {
                        console.log('🎯 Found employee for email:', email, employee);
                        return employee;
                    } else {
                        console.log('🎯 Creating placeholder for email:', email);
                        return { email, first_name: '', last_name: '' };
                    }
                });
                console.log('🎯 Setting participant objects from expansion:', participantObjects);
                setSelectedParticipants(participantObjects);
            } else {
                setSelectedParticipants(prefilledParticipants || []);
            }
            
            // Handle files from EventPreview
            if (existingEvent.uploadedFiles && Array.isArray(existingEvent.uploadedFiles)) {
                setUploadedFiles(existingEvent.uploadedFiles);
            } else {
                setUploadedFiles([]);
            }
            
            // Handle Zoom info from EventPreview
            if (existingEvent.zoomInfo) {
                console.log('🎯 Loading Zoom info from EventPreview expansion:', existingEvent.zoomInfo);
                setZoomInfo(existingEvent.zoomInfo);
                setIsConferencing(true);
            } else {
                setZoomInfo(null);
                setIsConferencing(false);
            }
            
            setIsRepeat(false);
            setRepeatFrequency('daily');
            setSelectedColor('#4263eb');
            setParticipantSearch('');
        } else {
            console.log('📋 Resetting form for new event');
            // Reset form for new event
            setTitle('');
            setDescription('');
            setLocation('');
            
            // Set internal time state from props
            console.log('📋 Setting internal time state from props:', { startTime, endTime });
            setInternalStartTime(startTime || '');
            setInternalEndTime(endTime || '');
            console.log('📋 Internal time state should now be:', { 
                internalStartTime: startTime || '', 
                internalEndTime: endTime || '' 
            });
            
            // Set internal date state from props
            setInternalDate(date || null);
            
            // For new tasks, default to timed events (not all-day) so users can edit times
            // Only set as all-day if explicitly specified with the all-day pattern
            const shouldBeAllDay = 
                (startTime === '00:00' && endTime === '23:59'); // Only explicit all-day pattern
            
            console.log('📋 All-day detection for new task:', { 
                startTime, 
                endTime, 
                shouldBeAllDay,
                reason: shouldBeAllDay ? 'Explicit all-day pattern (00:00-23:59)' : 'Default to timed event for editing'
            });
            setIsAllDay(shouldBeAllDay);
            
            setIsConferencing(false);
            setIsRepeat(false);
            setRepeatFrequency('daily');
            setSelectedColor('#4263eb');
            setParticipantSearch('');
            
            // Handle participants for new events
            let initialParticipants = prefilledParticipants || [];
            
            // If we have an employee prop (from time slot click) and it's not already in participants, add it
            if (employee && !initialParticipants.some(p => p.email === employee.email)) {
                console.log('🎯 Adding employee from time slot click as participant:', employee);
                initialParticipants = [employee, ...initialParticipants];
            }
            
            setSelectedParticipants(initialParticipants);
            setUploadedFiles([]);
            setZoomInfo(null);
            
            // Log participants setup
            console.log('🎯 Setting up participants for new event:', {
                employee: employee ? `${employee.first_name} ${employee.last_name} (${employee.email})` : 'none',
                prefilledParticipants: prefilledParticipants?.length || 0,
                finalParticipants: initialParticipants.length
            });
        }
    }, [isOpen, existingEvent, employees.length, prefilledParticipants, startTime, endTime, date, employee]); // Added employee to dependencies

    // Add/remove body class when right column state changes
    useEffect(() => {
        if (isRightColumn) {
            document.body.classList.add('right-column-active');
        } else {
            document.body.classList.remove('right-column-active');
        }

        // Cleanup on unmount
        return () => {
            document.body.classList.remove('right-column-active');
        };
    }, [isRightColumn]);

    if (!isOpen) return null;

    // Debug log to see the current internal time state
    console.log('🎭 EventPopup render - Current internal time state:', { 
        internalStartTime, 
        internalEndTime, 
        isAllDay 
    });

    // Color options for events
    const colors = ['#4263eb', '#7c3aed', '#dc2626', '#ea580c', '#ca8a04', '#16a34a'];

    const createZoomMeeting = async () => {
        if (!title.trim()) {
            alert('Please enter a title for the meeting.');
            return;
        }

        setIsCreatingZoom(true);
        
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('Authentication token not found.');
            }

            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://optivo-backend-production.up.railway.app';
            
            // Create proper Date objects from the props
            let meetingStartTime, meetingEndTime;
            
            if (date && startTime && endTime) {
                // date is a Date object, startTime and endTime are strings like "14:00"
                const baseDate = new Date(date);
                
                // Handle time strings like "14:00" or "14:30"
                const [startHour, startMinute] = startTime.split(':').map(Number);
                const [endHour, endMinute] = endTime.split(':').map(Number);
                
                meetingStartTime = new Date(baseDate);
                meetingStartTime.setHours(startHour, startMinute, 0, 0);
                
                meetingEndTime = new Date(baseDate);
                meetingEndTime.setHours(endHour, endMinute, 0, 0);
                
                console.log('📅 Parsed meeting times:', {
                    baseDate: baseDate.toISOString(),
                    startTime: startTime,
                    endTime: endTime,
                    meetingStartTime: meetingStartTime.toISOString(),
                    meetingEndTime: meetingEndTime.toISOString()
                });
            } else {
                // Fallback to current time + 30 minutes
                meetingStartTime = new Date();
                meetingEndTime = new Date(meetingStartTime.getTime() + 30 * 60 * 1000);
                
                console.log('📅 Using fallback times:', {
                    meetingStartTime: meetingStartTime.toISOString(),
                    meetingEndTime: meetingEndTime.toISOString()
                });
            }

            const duration = Math.round((meetingEndTime - meetingStartTime) / (1000 * 60)); // Duration in minutes

            console.log('🎯 Creating Zoom meeting with:', {
                title,
                startTime: meetingStartTime.toISOString(),
                duration: duration + ' minutes'
            });

            const response = await fetch(`${API_BASE_URL}/api/create`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    title: title,
                    startTime: meetingStartTime.toISOString(),
                    duration: duration
                })
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error('❌ Zoom API Error:', {
                    status: response.status,
                    statusText: response.statusText,
                    errorText: errorText
                });
                throw new Error(`Failed to create Zoom meeting: ${response.status} ${response.statusText}`);
            }

            const meetingData = await response.json();
            console.log('✅ Zoom meeting created successfully:', meetingData);

            // Store meeting info in state
            setZoomInfo({
                id: meetingData.id,
                passcode: meetingData.password,
                joinUrl: meetingData.join_url
            });
            
            setIsConferencing(true);
            console.log('🎉 Zoom meeting info stored in state');

        } catch (error) {
            console.error('❌ Error creating Zoom meeting:', error);
            alert(`Failed to create Zoom meeting: ${error.message}`);
        } finally {
            setIsCreatingZoom(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        console.log('🎭 EventPopup handleSubmit - Current state:', {
            isAllDay,
            startTime,
            endTime,
            title: title.trim(),
            employeeProp: employee,
            selectedParticipantsCount: selectedParticipants.length,
            selectedParticipants: selectedParticipants.map(p => ({ email: p.email, name: `${p.first_name} ${p.last_name}` }))
        });
        
        // Basic validation
        if (!title.trim()) {
            alert('Please enter a title for the event');
            return;
        }
        
        // Enhanced participant/employee validation with detailed logging
        let selectedEmployee = employee;
        console.log('🎯 Initial selectedEmployee from props:', selectedEmployee);
        
        if (!selectedEmployee && selectedParticipants.length > 0) {
            selectedEmployee = selectedParticipants[0];
            console.log('🎯 Using first participant as employee for new task:', selectedEmployee);
        }
        
        console.log('🎯 Final validation check:', {
            hasSelectedEmployee: !!selectedEmployee,
            selectedEmployeeEmail: selectedEmployee?.email,
            participantsCount: selectedParticipants.length,
            willPassValidation: !!selectedEmployee
        });
        
        if (!selectedEmployee) {
            console.error('❌ Validation failed - no employee or participants found:', {
                employeeProp: employee,
                selectedParticipants: selectedParticipants,
                selectedParticipantsLength: selectedParticipants.length
            });
            alert('Please add at least one participant to assign this task to someone');
            return;
        }

        // Create proper Date objects by combining date with time strings
        let startDateTime, endDateTime;
        
        const currentDate = internalDate || date;
        if (currentDate) {
            const baseDate = new Date(currentDate);
            
            if (isAllDay) {
                // For all-day events, set start to beginning of day and end to end of day
                startDateTime = new Date(baseDate);
                startDateTime.setHours(0, 0, 0, 0);
                
                endDateTime = new Date(baseDate);
                endDateTime.setHours(23, 59, 59, 999);
                
                console.log('📅 EventPopup created all-day Date objects:', {
                    baseDate: baseDate.toISOString(),
                    isAllDay: true,
                    startDateTime: startDateTime.toISOString(),
                    endDateTime: endDateTime.toISOString()
                });
            } else if (internalStartTime && internalEndTime) {
                // Parse time strings like "12:00"
                const [startHour, startMinute] = internalStartTime.split(':').map(Number);
                const [endHour, endMinute] = internalEndTime.split(':').map(Number);
                
                startDateTime = new Date(baseDate);
                startDateTime.setHours(startHour, startMinute, 0, 0);
                
                endDateTime = new Date(baseDate);
                endDateTime.setHours(endHour, endMinute, 0, 0);
                
                console.log('📅 EventPopup created timed Date objects:', {
                    baseDate: baseDate.toISOString(),
                    startTime: internalStartTime,
                    endTime: internalEndTime,
                    startDateTime: startDateTime.toISOString(),
                    endDateTime: endDateTime.toISOString()
                });
            } else {
                console.error('❌ Missing time information for timed event:', { internalStartTime, internalEndTime });
                alert('Please enter both start and end times for the event.');
                return;
            }
        } else {
            console.error('❌ Missing date information:', { date, internalDate, currentDate });
            alert('Invalid date information. Please try again.');
            return;
        }

        const eventData = {
            title: title.trim(),
            description,
            location,
            isAllDay,
            start: startDateTime,
            end: endDateTime,
            resource: selectedEmployee.id,
            isConferencing,
            isRepeat,
            color: selectedColor,
            backgroundColor: selectedColor,
            files: uploadedFiles, // Include files in event data
            zoom_info: zoomInfo, // Include Zoom meeting info
            participants: selectedParticipants.map(p => p.email),
            recurrence_rule: generateRecurrenceRule(repeatFrequency, moment(startDateTime).format('YYYY-MM-DD')),
            // Add required fields for Dashboard's handleScheduleCreate
            user_id: selectedEmployee.user_id,
            company_id: selectedEmployee.company_id,
            shift_date: moment(startDateTime).format('YYYY-MM-DD'),
            start_time: moment(startDateTime).format('HH:mm:ss'),
            end_time: moment(endDateTime).format('HH:mm:ss'),
            is_all_day: isAllDay,
            type: 'task'
        };

        console.log('🎭 EventPopup creating eventData:', {
            title: eventData.title,
            start: eventData.start,
            end: eventData.end,
            filesCount: uploadedFiles.length,
            zoomInfo: zoomInfo ? 'present' : 'null',
            participantsCount: selectedParticipants.length,
            isRepeat: eventData.isRepeat,
            repeatFrequency,
            recurrence_rule: eventData.recurrence_rule
        });

        try {
            await onSave(eventData);
            
            // Clear form after successful save
            setTitle('');
            setDescription('');
            setLocation('');
            setIsAllDay(false);
            setIsConferencing(false);
            setIsRepeat(false);
            setRepeatFrequency('daily');
            setSelectedColor('#4263eb');
            setParticipantSearch('');
            setSelectedParticipants([]);
            setUploadedFiles([]);
            setInternalStartTime('');
            setInternalEndTime('');
            setInternalDate(null);
            
            onClose();
        } catch (error) {
            console.error('Error saving event:', error);
            alert(error.message || 'Failed to save event. Please try again.');
        }
    };

    const handleDelete = async () => {
        try {
            if (!existingEvent?.id) {
                console.error('Cannot delete event: no event ID provided');
                return;
            }
            
            await onDelete(existingEvent.id);
            onClose();
        } catch (error) {
            console.error('Failed to delete event:', error);
            setError('Failed to delete event. Please try again.');
        }
    };

    const downloadFile = async (file, index) => {
        try {
            // Handle files that come from server (have URL)
            if (file.url || file.downloadUrl) {
                const link = document.createElement('a');
                link.href = file.url || file.downloadUrl;
                link.download = file.originalName || file.original_name || file.name || `file_${index}`;
                link.target = '_blank';
                // Add rel for security
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                return;
            }

            // Handle server files with s3Key (need to fetch from backend)
            if (file.s3Key || file.id) {
                const token = localStorage.getItem('token');
                const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://optivo-backend-production.up.railway.app';
                
                // Use the /download endpoint with query parameter - this is the most reliable approach
                let downloadUrl;
                if (file.s3Key) {
                    // Use the download endpoint with the s3Key as a query parameter
                    downloadUrl = `${API_BASE_URL}/api/files/download?key=${encodeURIComponent(file.s3Key)}`;
                    console.log('🔑 Using download endpoint with s3Key query for file:', file.originalName || file.original_name || file.name);
                } else if (file.id) {
                    // Use the file ID endpoint as fallback
                    downloadUrl = `${API_BASE_URL}/api/files/${file.id}/download`;
                    console.log('🆔 Using file ID approach for file:', file.originalName || file.original_name || file.name);
                } else {
                    console.warn('File has neither ID nor s3Key:', file);
                    throw new Error('Cannot download file: missing file ID and s3Key');
                }
                
                console.log('🔽 Downloading file from:', downloadUrl);
                
                const response = await fetch(downloadUrl, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
                }

                // Check if response is JSON (presigned URL) or binary data
                const contentType = response.headers.get('content-type');
                console.log('📋 Response content-type:', contentType);
                
                if (contentType && contentType.includes('application/json')) {
                    // Backend returned a presigned URL
                    const data = await response.json();
                    console.log('📦 Backend response data:', data);
                    
                    if (data.url) {
                        console.log('✅ Got presigned URL, downloading file directly');
                        // Use a hidden iframe for smoother download experience
                        const iframe = document.createElement('iframe');
                        iframe.style.display = 'none';
                        iframe.src = data.url;
                        document.body.appendChild(iframe);
                        
                        // Clean up the iframe after a short delay
                        setTimeout(() => {
                            if (document.body.contains(iframe)) {
                                document.body.removeChild(iframe);
                            }
                        }, 3000);
                        
                        console.log('✅ File download initiated via presigned URL:', file.originalName || file.original_name || file.name);
                    } else {
                        console.error('❌ No download URL in response:', data);
                        throw new Error('No download URL received from backend');
                    }
                } else {
                    // Backend returned file content directly
                    console.log('📦 Backend returned file content directly');
                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = file.originalName || file.original_name || file.name || `file_${index}`;
                    document.body.appendChild(link);
                    link.click();
                    
                    // Clean up
                    URL.revokeObjectURL(url);
                    document.body.removeChild(link);
                    
                    console.log('✅ File downloaded successfully:', file.originalName || file.original_name || file.name);
                }
                return;
            }

            // Handle newly uploaded files (have file blob)
            if (file.file) {
                const blob = new Blob([file.file], { type: file.type || file.file.type || 'application/octet-stream' });
                const url = URL.createObjectURL(blob);
                
                const link = document.createElement('a');
                link.href = url;
                link.download = file.originalName || file.original_name || file.name || file.file.name || `file_${index}`;
                document.body.appendChild(link);
                link.click();
                
                // Clean up
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                return;
            }

            // Fallback for other file structures
            if (file instanceof File) {
                const url = URL.createObjectURL(file);
                const link = document.createElement('a');
                link.href = url;
                link.download = file.name || `file_${index}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                return;
            }

            // If no recognized format, show error
            console.error('File format not supported for download:', file);
            alert('Unable to download this file. File format not supported.');
            
        } catch (error) {
            console.error('Error downloading file:', error);
            alert('Unable to download file. Please try again.');
        }
    };

    return ReactDOM.createPortal(
        <div 
            className={`event-popup-overlay ${isRightColumn ? 'right-column' : ''}`}
            onClick={onClose}
        >
            <div 
                className={`event-popup ${isRightColumn ? 'right-column' : ''}`}
                onClick={e => e.stopPropagation()}
            >
                <form onSubmit={handleSubmit} className="event-form">
                    <div className="event-form-left">
                        <div className="title-section">
                            <svg width="32" height="24" viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1.71429 0C1.25963 0 0.823594 0.180612 0.502103 0.502102C0.180612 0.823593 0 1.25963 0 1.71429C0 2.16894 0.180612 2.60498 0.502103 2.92647C0.823594 3.24796 1.25963 3.42857 1.71429 3.42857H30.2857C30.7404 3.42857 31.1764 3.24796 31.4979 2.92647C31.8194 2.60498 32 2.16894 32 1.71429C32 1.25963 31.8194 0.823593 31.4979 0.502102C31.1764 0.180612 30.7404 0 30.2857 0H1.71429ZM1.71429 6.85714C1.25963 6.85714 0.823594 7.03775 0.502103 7.35925C0.180612 7.68074 0 8.11677 0 8.57143C0 9.02609 0.180612 9.46212 0.502103 9.78361C0.823594 10.1051 1.25963 10.2857 1.71429 10.2857H30.2857C30.7404 10.2857 31.1764 10.1051 31.4979 9.78361C31.8194 9.46212 32 9.02609 32 8.57143C32 8.11677 31.8194 7.68074 31.4979 7.35925C31.1764 7.03775 30.7404 6.85714 30.2857 6.85714H1.71429ZM0 15.4286C0 14.9739 0.180612 14.5379 0.502103 14.2164C0.823594 13.8949 1.25963 13.7143 1.71429 13.7143H30.2857C30.7404 13.7143 31.1764 13.8949 31.4979 14.2164C31.8194 14.5379 32 14.9739 32 15.4286C32 15.8832 31.8194 16.3193 31.4979 16.6408C31.1764 16.9622 30.7404 17.1429 30.2857 17.1429H1.71429C1.25963 17.1429 0.823594 16.9622 0.502103 16.6408C0.180612 16.3193 0 15.8832 0 15.4286ZM1.71429 20.5714C1.25963 20.5714 0.823594 20.752 0.502103 21.0735C0.180612 21.395 0 21.8311 0 22.2857C0 22.7404 0.180612 23.1764 0.502103 23.4979C0.823594 23.8194 1.25963 24 1.71429 24H21.1429C21.5975 24 22.0335 23.8194 22.355 23.4979C22.6765 23.1764 22.8571 22.7404 22.8571 22.2857C22.8571 21.8311 22.6765 21.395 22.355 21.0735C22.0335 20.752 21.5975 20.5714 21.1429 20.5714H1.71429Z" fill="#A3A3A3"/>
                            </svg>
                            <div className="title-input-container">
                                <input
                                    type="text"
                                    className="title-input"
                                    placeholder="Add title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                />
                            </div>
                            {/* Commented out layout toggle button per user request
                            <button
                                type="button"
                                className={`layout-toggle-btn ${isRightColumn ? 'active' : ''}`}
                                onClick={() => setIsRightColumn(!isRightColumn)}
                                title={isRightColumn ? "Show as popup" : "Show in right column"}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 4H20V20H4V4Z M14 4V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                            </button>
                            */}
                        </div>

                        <div className="event-options">
                            <div className="event-options-row">
                                <div className="color-options">
                                    {colors.map((color) => (
                                        <button
                                            key={color}
                                            className={`color-dot ${selectedColor === color ? 'selected' : ''}`}
                                            style={{ backgroundColor: color }}
                                            onClick={() => setSelectedColor(color)}
                                        />
                                    ))}
                                </div>
                                <div className="toggle-options">
                                    <div className="toggle-switch">
                                        <label className="switch">
                                            <input
                                                type="checkbox"
                                                checked={isAllDay}
                                                onChange={() => setIsAllDay(!isAllDay)}
                                            />
                                            <span className="slider"></span>
                                        </label>
                                        <span className="toggle-label">All-day</span>
                                    </div>
                                    <button
                                        type="button"
                                        className={`toggle-btn ${isRepeat ? 'active' : ''}`}
                                        onClick={handleRepeatClick}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M17 1L21 5L17 9"/>
                                            <path d="M3 11V9A4 4 0 0 1 7 5H21"/>
                                            <path d="M7 23L3 19L7 15"/>
                                            <path d="M21 13V15A4 4 0 0 1 17 19H3"/>
                                        </svg>
                                        Repeat
                                    </button>
                                    
                                    {/* Repeat Options Dropdown */}
                                    {isRepeat && (
                                        <div className={`repeat-dropdown ${showRepeatDropdown ? 'open' : ''}`} ref={repeatDropdownRef}>
                                            <div className="repeat-dropdown-header">
                                                <span className="repeat-frequency-label">
                                                    {repeatOptions.find(opt => opt.value === repeatFrequency)?.label || 'Every day'}
                                                </span>
                                                <button
                                                    type="button"
                                                    className="repeat-dropdown-toggle"
                                                    onClick={() => setShowRepeatDropdown(!showRepeatDropdown)}
                                                >
                                                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>
                                                </button>
                                            </div>
                                            
                                            {showRepeatDropdown && (
                                                <div className="repeat-options-list">
                                                    {repeatOptions.map((option) => (
                                                        <div
                                                            key={option.value}
                                                            className={`repeat-option ${repeatFrequency === option.value ? 'selected' : ''}`}
                                                            onClick={() => handleRepeatFrequencySelect(option.value)}
                                                        >
                                                            {option.label}
                                                            {repeatFrequency === option.value && (
                                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                </svg>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="participants-section">
                            <div className="participants-input-row">
                                <svg width="30" height="29" viewBox="0 0 30 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8.57143 11.6C10.087 11.6 11.5404 10.9889 12.612 9.90122C13.6837 8.81351 14.2857 7.33826 14.2857 5.8C14.2857 4.26174 13.6837 2.78649 12.612 1.69878C11.5404 0.611069 10.087 0 8.57143 0C7.05591 0 5.60245 0.611069 4.53082 1.69878C3.45918 2.78649 2.85714 4.26174 2.85714 5.8C2.85714 7.33826 3.45918 8.81351 4.53082 9.90122C5.60245 10.9889 7.05591 11.6 8.57143 11.6ZM21.4286 11.6C22.5652 11.6 23.6553 11.1417 24.459 10.3259C25.2628 9.51013 25.7143 8.40369 25.7143 7.25C25.7143 6.09631 25.2628 4.98987 24.459 4.17409C23.6553 3.3583 22.5652 2.9 21.4286 2.9C20.2919 2.9 19.2018 3.3583 18.3981 4.17409C17.5944 4.98987 17.1429 6.09631 17.1429 7.25C17.1429 8.40369 17.5944 9.51013 18.3981 10.3259C19.2018 11.1417 20.2919 11.6 21.4286 11.6ZM3.21429 14.5C2.3618 14.5 1.54424 14.8437 0.941442 15.4556C0.338647 16.0674 0 16.8972 0 17.7625V18.125C0 18.125 0 24.65 8.57143 24.65C10.5571 24.65 12.0829 24.3006 13.2543 23.7612C12.9901 22.8739 12.8562 21.9519 12.8571 21.025C12.8571 18.6224 13.7429 16.43 15.2 14.7654C14.799 14.5881 14.366 14.4977 13.9286 14.5H3.21429ZM30 21.025C30 18.9099 29.1722 16.8814 27.6987 15.3858C26.2252 13.8902 24.2267 13.05 22.1429 13.05C20.059 13.05 18.0605 13.8902 16.587 15.3858C15.1135 16.8814 14.2857 18.9099 14.2857 21.025C14.2857 23.1401 15.1135 25.1686 16.587 26.6642C18.0605 28.1598 20.059 29 22.1429 29C24.2267 29 26.2252 28.1598 27.6987 26.6642C29.1722 25.1686 30 23.1401 30 21.025ZM22.8571 21.75L22.8586 25.3794C22.8586 25.5716 22.7833 25.756 22.6494 25.892C22.5154 26.028 22.3337 26.1044 22.1443 26.1044C21.9548 26.1044 21.7732 26.028 21.6392 25.892C21.5053 25.756 21.43 25.5716 21.43 25.3794V21.75H17.8514C17.662 21.75 17.4803 21.6736 17.3464 21.5377C17.2124 21.4017 17.1371 21.2173 17.1371 21.025C17.1371 20.8327 17.2124 20.6483 17.3464 20.5123C17.4803 20.3764 17.662 20.3 17.8514 20.3H21.4286V16.675C21.4286 16.4827 21.5038 16.2983 21.6378 16.1623C21.7717 16.0264 21.9534 15.95 22.1429 15.95C22.3323 15.95 22.514 16.0264 22.6479 16.1623C22.7819 16.2983 22.8571 16.4827 22.8571 16.675V20.3H26.4243C26.6137 20.3 26.7954 20.3764 26.9294 20.5123C27.0633 20.6483 27.1386 20.8327 27.1386 21.025C27.1386 21.2173 27.0633 21.4017 26.9294 21.5377C26.7954 21.6736 26.6137 21.75 26.4243 21.75H22.8571Z" fill="#A3A3A3"/>
                                </svg>
                                <div className="participants-input-container">
                                    <input
                                        ref={participantInputRef}
                                        type="text"
                                        className="participant-search"
                                        placeholder="Add participants"
                                        value={participantSearch}
                                        onChange={(e) => setParticipantSearch(e.target.value)}
                                        onFocus={handleParticipantInputFocus}
                                        onBlur={handleParticipantInputBlur}
                                    />
                                    
                                    {/* Participants Dropdown */}
                                    {showParticipantsDropdown && filteredEmployees.length > 0 && (
                                        <div className="participants-dropdown">
                                            {filteredEmployees.map((employee) => (
                                                <div
                                                    key={employee.id}
                                                    className="participant-dropdown-item"
                                                    onClick={() => handleParticipantSelect(employee)}
                                                >
                                                    <div className="participant-info">
                                                        <div className="participant-name">
                                                            {employee.first_name && employee.last_name 
                                                                ? `${employee.first_name} ${employee.last_name}`
                                                                : employee.email
                                                            }
                                                        </div>
                                                        <div className="participant-email">
                                                            {employee.email}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    
                                    {/* Selected Participants */}
                                    {selectedParticipants.length > 0 && (
                                        <div className="selected-participants-container">
                                            {selectedParticipants.map((participant) => (
                                                <div key={participant.email} className="selected-participant">
                                                    <div className="participant-info">
                                                        <span className="participant-name">
                                                            {participant.first_name && participant.last_name 
                                                                ? `${participant.first_name} ${participant.last_name}`
                                                                : participant.email
                                                            }
                                                        </span>
                                                        <span className="participant-role">
                                                            {participant.email}
                                                        </span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        className="remove-participant-btn"
                                                        onClick={() => handleParticipantRemove(participant.email)}
                                                        title="Remove participant"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="description-group">
                            <svg width="32" height="24" viewBox="0 0 32 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1.71429 0C1.25963 0 0.823594 0.180612 0.502103 0.502102C0.180612 0.823593 0 1.25963 0 1.71429C0 2.16894 0.180612 2.60498 0.502103 2.92647C0.823594 3.24796 1.25963 3.42857 1.71429 3.42857H30.2857C30.7404 3.42857 31.1764 3.24796 31.4979 2.92647C31.8194 2.60498 32 2.16894 32 1.71429C32 1.25963 31.8194 0.823593 31.4979 0.502102C31.1764 0.180612 30.7404 0 30.2857 0H1.71429ZM1.71429 6.85714C1.25963 6.85714 0.823594 7.03775 0.502103 7.35925C0.180612 7.68074 0 8.11677 0 8.57143C0 9.02609 0.180612 9.46212 0.502103 9.78361C0.823594 10.1051 1.25963 10.2857 1.71429 10.2857H30.2857C30.7404 10.2857 31.1764 10.1051 31.4979 9.78361C31.8194 9.46212 32 9.02609 32 8.57143C32 8.11677 31.8194 7.68074 31.4979 7.35925C31.1764 7.03775 30.7404 6.85714 30.2857 6.85714H1.71429ZM0 15.4286C0 14.9739 0.180612 14.5379 0.502103 14.2164C0.823594 13.8949 1.25963 13.7143 1.71429 13.7143H30.2857C30.7404 13.7143 31.1764 13.8949 31.4979 14.2164C31.8194 14.5379 32 14.9739 32 15.4286C32 15.8832 31.8194 16.3193 31.4979 16.6408C31.1764 16.9622 30.7404 17.1429 30.2857 17.1429H1.71429C1.25963 17.1429 0.823594 16.9622 0.502103 16.6408C0.180612 16.3193 0 15.8832 0 15.4286ZM1.71429 20.5714C1.25963 20.5714 0.823594 20.752 0.502103 21.0735C0.180612 21.395 0 21.8311 0 22.2857C0 22.7404 0.180612 23.1764 0.502103 23.4979C0.823594 23.8194 1.25963 24 1.71429 24H21.1429C21.5975 24 22.0335 23.8194 22.355 23.4979C22.6765 23.1764 22.8571 22.7404 22.8571 22.2857C22.8571 21.8311 22.6765 21.395 22.355 21.0735C22.0335 20.752 21.5975 20.5714 21.1429 20.5714H1.71429Z" fill="#A3A3A3"/>
                            </svg>
                            <textarea
                                placeholder="Add description"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>

                        <div className="flex justify-end space-x-2" style={{ marginTop: '-30px' }}>
                            <div className={`button-group ${existingEvent && !existingEvent.isNew ? 'editing' : ''}`} style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'center', width: '100%' }}>
                                <button
                                    type="submit"
                                    className="save-btn-outline"
                                    style={{ flex: '1', minWidth: '0' }}
                                >
                                    {existingEvent && !existingEvent.isNew ? 'Update' : 'Save Event'}
                                </button>
                                {existingEvent && !existingEvent.isNew && onDelete && (
                                    <button
                                        type="button"
                                        onClick={handleDelete}
                                        className="delete-btn"
                                        style={{ flex: '1', minWidth: '0' }}
                                    >
                                        Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="event-form-right">
                        <div className="datetime-section">
                            <div className="time-icon">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10"/>
                                    <polyline points="12 6 12 12 16 14"/>
                                </svg>
                            </div>
                            
                            {/* Date Input Field */}
                            <div className="date-input-group">
                                <input
                                    type="date"
                                    value={internalDate ? moment(internalDate).format('YYYY-MM-DD') : (date ? moment(date).format('YYYY-MM-DD') : '')}
                                    onChange={(e) => {
                                        const newDate = new Date(e.target.value);
                                        setInternalDate(newDate);
                                        console.log('Date changed:', e.target.value);
                                    }}
                                />
                            </div>
                            
                            {/* Time Input Fields inside the same box */}
                            {!isAllDay && (
                                <div className="inline-time-inputs">
                                    <div className="time-input-group">
                                        <input
                                            id="start-time"
                                            type="time"
                                            value={internalStartTime}
                                            onChange={(e) => {
                                                setInternalStartTime(e.target.value);
                                                console.log('Start time changed:', e.target.value);
                                            }}
                                            placeholder="Start time"
                                        />
                                    </div>
                                    <span className="time-separator">→</span>
                                    <div className="time-input-group">
                                        <input
                                            id="end-time"
                                            type="time"
                                            value={internalEndTime}
                                            onChange={(e) => {
                                                setInternalEndTime(e.target.value);
                                                console.log('End time changed:', e.target.value);
                                            }}
                                            placeholder="End time"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Video conferencing input group - styled like location */}
                        {!zoomInfo && (
                            <div className="input-group" style={{ cursor: 'pointer' }} onClick={() => {
                                if (!isConferencing && !zoomInfo) {
                                    createZoomMeeting();
                                }
                            }}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M23 7L16 12L23 17V7Z"/>
                                    <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                                </svg>
                                <input
                                    type="text"
                                    placeholder={isCreatingZoom ? "Creating meeting..." : "Add video conferencing"}
                                    value=""
                                    readOnly
                                    disabled={isCreatingZoom}
                                    style={{ cursor: 'pointer', backgroundColor: 'transparent' }}
                                />
                            </div>
                        )}
                        
                        {/* Show Zoom meeting details if available */}
                        {zoomInfo && isConferencing && (
                            <div className="zoom-info-compact">
                                <button 
                                    type="button"
                                    className="join-zoom-btn"
                                    onClick={() => {
                                        const joinUrl = zoomInfo.join_url || zoomInfo.joinUrl;
                                        if (joinUrl) {
                                            console.log('Opening Zoom URL:', joinUrl);
                                            window.open(joinUrl, '_blank', 'noopener,noreferrer');
                                        } else {
                                            console.error('No join URL found in zoomInfo:', zoomInfo);
                                        }
                                    }}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M23 7L16 12L23 17V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    Join Zoom Meeting
                                </button>
                                <div className="zoom-details-compact">
                                    <div className="zoom-detail-item">
                                        <span className="zoom-label">Meeting ID:</span>
                                        <span className="zoom-value">{zoomInfo.meeting_id || zoomInfo.id}</span>
                                    </div>
                                    <div className="zoom-detail-item">
                                        <span className="zoom-label">Passcode:</span>
                                        <span className="zoom-value">{zoomInfo.passcode}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="input-group">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                                <circle cx="12" cy="10" r="3"/>
                            </svg>
                            <input
                                type="text"
                                placeholder="Add location"
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                            />
                        </div>
                        <div className="docs-section">
                            <h3>Attachments</h3>
                            {uploadedFiles.length === 0 ? (
                                <div 
                                    className={`file-upload-area ${isDragOver ? 'drag-over' : ''}`}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        onChange={handleFileInputChange}
                                        style={{ display: 'none' }}
                                    />
                                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                                        <polyline points="17 8 12 3 7 8"/>
                                        <line x1="12" y1="3" x2="12" y2="15"/>
                                    </svg>
                                    <p>Drop files here or click to upload</p>
                                </div>
                            ) : (
                                <div className="uploaded-files-grid">
                                    {uploadedFiles.map((file, index) => (
                                        <div key={index} className="file-item">
                                            <div className="file-info" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px', width: '100%' }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                                                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                                                    <polyline points="14 2 14 8 20 8"/>
                                                    <line x1="16" y1="13" x2="8" y2="13"/>
                                                    <line x1="16" y1="17" x2="8" y2="17"/>
                                                    <polyline points="10 9 9 9 8 9"/>
                                                </svg>
                                                <div className="file-details" style={{ display: 'flex', flexDirection: 'column', flex: '1' }}>
                                                    <span className="file-name">
                                                        {file.originalName || file.name}
                                                    </span>
                                                    <span className="file-size">
                                                        {formatFileSize(file.size)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <button
                                                    type="button"
                                                    className="download-file-btn"
                                                    onClick={() => downloadFile(file, index)}
                                                    title="Download file"
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: '#666' }}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                                                        <polyline points="7 10 12 15 17 10"/>
                                                        <line x1="12" y1="15" x2="12" y2="3"/>
                                                    </svg>
                                                </button>
                                                <button
                                                    type="button"
                                                    className="remove-file-btn"
                                                    onClick={() => removeFile(index)}
                                                    title="Remove file"
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        className="add-more-files-btn"
                                        onClick={() => fileInputRef.current?.click()}
                                    >
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <line x1="12" y1="5" x2="12" y2="19"/>
                                            <line x1="5" y1="12" x2="19" y2="12"/>
                                        </svg>
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        onChange={handleFileInputChange}
                                        style={{ display: 'none' }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
});

export default EventPopup; 